import { useState, useMemo } from 'react';

const BRANDFETCH_CLIENT_ID = process.env.REACT_APP_BRANDFETCH_CLIENT_ID;

/** Extract a bare domain (no protocol / path / www) from a website string. */
function toDomain(website) {
  if (!website) return null;
  try {
    const url = website.includes('://') ? website : `https://${website}`;
    const host = new URL(url).hostname.replace(/^www\./, '');
    return host.includes('.') ? host : null;
  } catch {
    return null;
  }
}

/**
 * Company logo tile. Prefers the stored logo_url, else Brandfetch's official
 * brand-asset CDN resolved from the company website, else a category-tinted
 * monogram (the original look). Fixed size → no layout shift.
 */
export default function CompanyLogo({ name, website, logoUrl, accent = 'var(--tandm-accent)', size = 44 }) {
  const [failed, setFailed] = useState(false);

  const src = useMemo(() => {
    if (logoUrl) return logoUrl;
    const domain = toDomain(website);
    if (domain && BRANDFETCH_CLIENT_ID) {
      const px = Math.max(48, Math.round(size * 2));
      // Brandfetch Logo Link: /domain/<domain> + path params. fallback/404 → when
      // no real logo exists, the image 404s and we show our own monogram instead
      // of Brandfetch's generic lettermark.
      return `https://cdn.brandfetch.io/domain/${domain}/w/${px}/h/${px}/fallback/404?c=${BRANDFETCH_CLIENT_ID}`;
    }
    return null;
  }, [logoUrl, website, size]);

  const showImg = src && !failed;

  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: 12,
        flexShrink: 0,
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: showImg ? 'var(--tandm-surface)' : `linear-gradient(135deg, ${accent}18, ${accent}32)`,
        border: showImg ? '1px solid var(--tandm-line)' : `1px solid ${accent}28`,
      }}
    >
      {showImg ? (
        <img
          src={src}
          alt={name ? `${name} logo` : 'Company logo'}
          loading="lazy"
          onError={() => setFailed(true)}
          style={{ width: '100%', height: '100%', objectFit: 'contain', padding: size * 0.14 }}
        />
      ) : (
        <span style={{ fontSize: size * 0.39, fontWeight: 800, color: accent, fontFamily: 'var(--tandm-font-sans)' }}>
          {name?.charAt(0)?.toUpperCase() || '?'}
        </span>
      )}
    </div>
  );
}
